/**
 * Resolve a data-tutorial target to viewport coordinates.
 *
 * Hooks are additive `data-tutorial="..."` attributes on existing elements
 * (zero visual change). Lazy views mount late: `waitFor` retries for up to
 * ~2s (art spec §2). If the target never mounts, the caller falls back to a
 * centered panel — never a dangling arrow (§6).
 */

const activeWaits = new Set()

function findTarget(name) {
    return document.querySelector(`[data-tutorial="${name}"]`)
}

/**
 * Resolve target rect. Resolves to:
 *   { ok: true, rect } | { ok: false, reason: 'missing' }
 * Never throws.
 */
export function resolveTargetRect(name) {
    try {
        const el = findTarget(name)
        if (!el) return { ok: false, reason: 'missing' }
        const r = el.getBoundingClientRect()
        const visible = r.width > 0 && r.height > 0
        if (!visible) return { ok: false, reason: 'missing' }
        return { ok: true, rect: { x: r.left, y: r.top, width: r.width, height: r.height } }
    } catch {
        return { ok: false, reason: 'missing' }
    }
}

/**
 * Resolve with retry for lazy-mounted views.
 * @param {string} name data-tutorial hook
 * @param {number} timeoutMs total budget (default 2000)
 */
export function waitForTarget(name, timeoutMs = 2000) {
    if (activeWaits.has(name)) return Promise.resolve({ ok: false, reason: 'busy' })
    activeWaits.add(name)
    const deadline = Date.now() + timeoutMs
    return new Promise((resolve) => {
        const tick = () => {
            const res = resolveTargetRect(name)
            if (res.ok || Date.now() > deadline) {
                activeWaits.delete(name)
                resolve(res)
                return
            }
            requestAnimationFrame(tick)
        }
        tick()
    })
}

/** Click through to the element behind the hook (CTA action 'click'). */
export function clickTarget(name) {
    const el = findTarget(name)
    if (!el) return false
    el.click()
    return true
}

/**
 * Convert a viewport-space rect into page-space (documents scroll, the app's
 * content column scrolls — the spotlight layers are position:fixed, so this
 * is only needed if we ever move to absolute positioning).
 */
export function rectToViewport(rect) {
    return {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
    }
}
